import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { errorText } from '../../utils/errorText';
import { canUseWorkspace } from '../../utils/workspace';
import { can } from '../../utils/permissions';
import useAddressSheet from '../../hooks/useAddressSheet';
import Sheet from '../ui/Sheet';
import ThermalStrip from '../thermal/ThermalStrip';
import { fieldStore } from '../../utils/fieldBatches';
import { fieldOutbox } from '../../utils/fieldOutbox';
import useUnsentBatches from '../field/useUnsentBatches';
import OrganicLoader from '../organic/OrganicLoader.jsx';
import PipelineCanvas from './PipelineCanvas';
import CanvasModeSwitch from './CanvasModeSwitch';
import IsolatePipelines from './IsolatePipelines';
import IsolatePeek from './IsolatePeek';
import IsolatesTable from './IsolatesTable';
import QuickActionDrawer from './QuickActionDrawer';
import IntakeDrawer from './IntakeDrawer';
import AddIsolateModal, { canAdd } from './AddIsolateModal';
import IntakeRecordDrawer from '../intake/IntakeRecordDrawer';
import useIsolatesOverview from './useIsolatesOverview';
import useDossiers from './useDossiers';
import { canDo, canSeeHub, nodeParams, selectedNode } from './stages';
import { submissionKeys } from './quickActions';
import * as S from '../specimens/styles';

/*
 * The Active Isolates Workspace: Home's hub.
 *
 * Above, the n8n canvas (PipelineCanvas): every node a gateway, counted
 * from GET /specimens/hub. Below, one master table of every isolate the
 * reader may see (IsolatesTable, from GET /specimens/overview), there from
 * the start; a node or a count filters its rows -- in place, never a trip
 * through the menus -- and pressing the node again shows them all. Its rows
 * open into the isolate's dossier, and a drawer of quick forms
 * (QuickActionDrawer) does the work a row needs next.
 *
 * Everything a reader chose lives in the URL (?view=&node=&state=&unit=
 * &origin=&q=&sort=&order=&page=&open=&tab=&canvas=&hl=), written with
 * replace: a reload or a shared link opens the same filter on the same row,
 * and Back leaves Home rather than undoing a filter. No view= is the whole
 * table ("all").
 *
 * The canvas has two views (CanvasModeSwitch). The summary is the n8n
 * canvas above. canvas=isolates draws instead one pipeline per isolate
 * (IsolatePipelines) -- the same page of the same rows as the table, from
 * the same request -- and switching to it filters both to the isolates in
 * culture (view=lab, no node: the server's "active" population). Pressing
 * a pipeline highlights its table row (hl=) and brings it into view. The
 * last choice is remembered on this device (mb_hub_canvas) for a bare Home.
 *
 * An intake record opens over all of it, in its own drawer
 * (IntakeRecordDrawer), from ?intake=<external_id>: Home is where intakes are
 * read and edited, and every link to one (a code lookup, the scan station, a
 * row's gallery link, the Taxonomy tab, the old /external address) leads
 * here. It is not a filter: a node or a count leaves it open, and closing
 * the drawer takes only it away.
 *
 * The header's "+ Add isolate / dish" (AddIsolateModal) is every way in at
 * once: preservation (bank stock), an external reception, or a field
 * collection saved as a Field Mode batch -- each card to whoever the server
 * lets do it. The Intake node's own "New intake" stays.
 *
 * Someone with no lab visibility (lab_scope 'none': a collector, and the
 * research and audit roles) gets "My isolates" instead: the collection
 * trigger and their own isolates, without lab and bank lanes -- or culture
 * and bank columns -- that would have nothing behind them. No view switch
 * either, and a canvas= in the URL is ignored.
 *
 * A row's Lab analytics tray (LabAnalyticsTray) opens its Thermal captures
 * here, in a sheet over the hub: ?tool=thermal&for=<specimen_id>
 * (hooks/useAddressSheet.js). Unlike the filters, opening it pushes a history
 * entry, so the phone's Back closes the sheet and leaves the hub as it was.
 * Only for lab readers (lab:read); for anyone else the address opens nothing.
 */
const PARAMS = ['view', 'node', 'state', 'unit', 'origin', 'q', 'sort', 'order', 'page', 'open', 'tab', 'canvas', 'hl',
  'peek', 'tool', 'for'];
const VIEWS = ['all', 'lab', 'bank', 'intake', 'mine'];
// What a node or a count clears: every filter and the highlight, but not the
// canvas's view -- that is how the reader looks, not what they filter -- nor a
// tool's sheet, which its own Back closes (useAddressSheet).
const FILTER_PARAMS = PARAMS.filter((k) => !['canvas', 'tool', 'for'].includes(k));
// Switching to the isolates view: the table's rows become the active
// isolates (view=lab with no node, state, unit or origin), from their first
// page. The search and sort stay.
const TO_ISOLATES = {
  canvas: 'isolates', view: 'lab', node: null, state: null, unit: null, origin: null, page: null, open: null,
  tab: null, hl: null,
};
// The same, as the hub reads its URL (an absent parameter is '').
const RESTORED = Object.fromEntries(Object.entries(TO_ISOLATES).map(([k, v]) => [k, v ?? '']));
// A link that names any of these opens what it names: the remembered view
// applies only to a bare Home (a search or sort aside).
const LINK_PARAMS = ['view', 'node', 'state', 'unit', 'origin', 'page', 'open', 'tab', 'canvas', 'hl', 'peek', 'tool', 'for'];
// The sheets a row's tray opens over the hub (?tool=), and what each is open on (?for=).
const TOOL_SHEETS = Object.freeze(['thermal']);
const NO_TOOL_SHEETS = Object.freeze([]);
const TOOL_EXTRA = Object.freeze(['for']);

const CANVAS_KEY = 'mb_hub_canvas';
/** The canvas view this device last chose; '' without storage. */
function storedCanvas() {
  try {
    return window.localStorage.getItem(CANVAS_KEY) || '';
  } catch {
    return '';
  }
}
function storeCanvas(mode) {
  try {
    window.localStorage.setItem(CANVAS_KEY, mode);
  } catch {
    // No storage (a private window, blocked site data): the URL still says it.
  }
}

export default function IsolateHub({ store: injectedStore, outbox: injectedOutbox, reveal = null }) {
  // Every catalogue a table, tab or drawer says something in, loaded with the
  // hub: taxonomy's for a dictionary entry's trophic mode; scan's for the
  // micrograph upload and viewer the culture card borrows from the station.
  const { t } = useTranslation(['isolates', 'specimens', 'plates', 'slants', 'dna', 'taxonomy', 'scan', 'cultureAnalysis', 'common']);
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const visible = canSeeHub(user);

  const [hub, setHub] = useState(null);
  const [hubError, setHubError] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [action, setAction] = useState(null);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [toast, setToast] = useState('');
  const toastTimer = useRef(null);
  const statusKeys = useRef(submissionKeys('hub-status'));

  const urlParams = useMemo(() => Object.fromEntries(PARAMS.map((k) => [k, search.get(k) || ''])), [search]);
  const mineOnly = hub?.lab_scope === 'none';

  const onParams = useCallback((patch) => {
    setSearch((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === null || v === undefined || v === '') next.delete(k);
        else next.set(k, String(v));
      });
      return next;
    }, { replace: true });
  }, [setSearch]);

  const loadHub = useCallback(() => {
    if (!visible) return;
    api.get('/specimens/hub')
      .then((res) => { setHub(res?.data || null); setHubError(''); })
      .catch((e) => {
        if (e?.response?.status === 403) setForbidden(true);
        else setHubError(errorText(e));
      });
  }, [visible]);
  useEffect(() => { loadHub(); }, [loadHub]);

  // The canvas view this device last chose, read once. A bare Home (its
  // link names no filter, row or view) opens the way it was left: once the
  // counts say the reader has lab visibility, the view is written into the
  // URL -- and read as if it were there already until the router catches up,
  // so the table asks the server once, for the rows that view shows, and
  // the summary never flashes first.
  const [remembered] = useState(storedCanvas);
  const [restore, setRestore] = useState('unchecked'); // then 'writing', then 'done'
  const bare = LINK_PARAMS.every((k) => !urlParams[k]);
  const restoring = bare && !!hub && !mineOnly && remembered === 'isolates' && restore !== 'done';
  useEffect(() => {
    if (!hub || restore === 'done') return;
    if (restore === 'unchecked' && restoring) {
      onParams(TO_ISOLATES);
      setRestore('writing');
    } else if (!restoring) {
      setRestore('done');
    }
  }, [hub, restore, restoring, onParams]);
  const params = useMemo(() => (restoring ? { ...urlParams, ...RESTORED } : urlParams), [restoring, urlParams]);

  // With no view chosen, the whole table -- once the counts say whose: a
  // reader without lab visibility gets their own isolates instead.
  const view = VIEWS.includes(params.view) ? params.view : (hub ? (mineOnly ? 'mine' : 'all') : '');
  const isolatesMode = !!hub && !mineOnly && params.canvas === 'isolates';

  const overview = useIsolatesOverview({ ...params, view: view || 'all' }, { enabled: visible && !!view && !forbidden });
  const dossiers = useDossiers();
  const { ensure, reconcile } = dossiers;
  useEffect(() => { if (params.open) ensure(params.open); }, [params.open, ensure]);
  // The rows were read again (another page, a filter, an intake edited, an
  // action's own row): a dossier read earlier that no longer tells the row's
  // story is read again, so an open row or stage never contradicts its row.
  useEffect(() => { reconcile(overview.items); }, [overview.items, reconcile]);

  // This device's Field Mode records not yet sent: the Collection node says
  // how many, as the map does with its grey pins.
  const store = useMemo(() => injectedStore || fieldStore(), [injectedStore]);
  // Field Mode's outbox, for a field collection made here ("Add isolate /
  // dish"): asked for only when one is saved, so Home starts no sender of
  // its own by being opened.
  const outbox = useMemo(() => injectedOutbox || {
    run: () => fieldOutbox().run(), start: () => fieldOutbox().start(), signedIn: () => fieldOutbox().signedIn(),
  }, [injectedOutbox]);
  const unsent = useUnsentBatches(store, visible ? user?.user_id : null).filter((b) => b.status !== 'active');

  // The tray's Thermal sheet, in the address. Its isolate is the row on this
  // page, else (a link, a reload on another page) what its dossier says.
  const labReader = can(user, 'lab:read');
  const toolSheet = useAddressSheet('tool', labReader ? TOOL_SHEETS : NO_TOOL_SHEETS, { extra: TOOL_EXTRA });
  const { open: openToolSheet } = toolSheet;
  const thermalFor = toolSheet.sheet === 'thermal' ? toolSheet.values.for : '';
  useEffect(() => { if (thermalFor) ensure(thermalFor); }, [thermalFor, ensure]);
  const onThermal = useCallback((item) => openToolSheet('thermal', { for: item.specimen_id }), [openToolSheet]);

  useEffect(() => () => clearTimeout(toastTimer.current), []);
  const say = useCallback((message) => {
    setToast(message || '');
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 6000);
  }, []);

  /** After any write: the row, its dossier and the counts are read again. */
  const refreshAfter = useCallback((item) => {
    if (item?.specimen_id) {
      overview.refreshRows([item.specimen_id]);
      dossiers.refresh(item.specimen_id);
    }
    loadHub();
  }, [overview, dossiers, loadHub]);

  const closeDrawer = useCallback(() => setAction(null), []);

  const onAction = useCallback((key, item, extra = {}) => {
    if (key === 'quickStatus') {
      // One tap records a growth stage. "Ready to transfer" needs where to,
      // so that one opens the full check form, already set.
      if (extra.status === 'ready_to_transfer') {
        setAction({ key: 'logCheck', item, extra: { plateId: extra.plateId, status: extra.status } });
        ensure(item.specimen_id);
        return;
      }
      const path = `/plates/${extra.plateId}/checks`;
      const body = { status: extra.status };
      api.post(path, body, { headers: { 'Idempotency-Key': statusKeys.current.keyFor(path, body) } })
        .then(() => {
          statusKeys.current.reset();
          say(t('toast.statusSet', { status: t(`plates:plateStatus.${extra.status}`) }));
          refreshAfter(item);
        })
        .catch((e) => say(errorText(e)));
      return;
    }
    setAction({ key, item, extra });
    ensure(item.specimen_id);
  }, [ensure, say, refreshAfter, t]);

  const onDone = useCallback((message) => {
    const item = action?.item;
    setAction(null);
    if (message) say(message);
    refreshAfter(item);
  }, [action, say, refreshAfter]);

  // An isolate archived from its expanded row (IsolateDetails): it is no
  // longer active, so the hub's lists leave it out -- the page and the
  // counts are read again, and its row closes rather than pointing at nothing.
  const onArchived = useCallback((item) => {
    say(t('toast.archived', { code: item?.specimen_code || '' }));
    onParams({ open: null, hl: null, tab: null, peek: null });
    overview.reload?.();
    loadHub();
  }, [say, t, onParams, overview, loadHub]);

  // An intake opens here, in its drawer, over the hub as it is (its filters
  // kept); anything else is another page.
  const intakeId = search.get('intake') || '';
  const go = useCallback((path, state) => {
    if (typeof path === 'string' && path.startsWith('/?')) {
      const target = new URLSearchParams(path.slice(2));
      if (target.get('intake') && [...target.keys()].length === 1) {
        setAction(null);
        onParams({ intake: target.get('intake') });
        return;
      }
    }
    navigate(path, state ? { state } : undefined);
  }, [navigate, onParams]);
  const closeIntake = useCallback(() => onParams({ intake: null }), [onParams]);
  // Something written on the intake: its isolate's row, and the counts.
  const onIntakeChanged = useCallback(() => {
    overview.reload?.();
    loadHub();
  }, [overview, loadHub]);

  // A row pressed in the isolates view: its table row is highlighted, and
  // brought into view on every press (the nonce), not only the first.
  // Pressing the highlighted one lifts the highlight. A press counts once
  // the URL names the same row: the router may commit its update a render
  // after this state's, and the old row must not be scrolled to meanwhile.
  const highlightId = params.hl || null;
  const [press, setPress] = useState({ id: null, n: 0 });
  // Pressing an isolate's name opens its row in the table as well as
  // highlighting it (2026-10-12): the name leads to the row's own details.
  const onHighlight = useCallback((id) => {
    const next = params.hl === id ? null : id;
    onParams(next ? { hl: next, open: next } : { hl: null });
    setPress((p) => ({ id: next, n: p.n + 1 }));
  }, [onParams, params.hl]);
  const highlightNonce = press.id && press.id === highlightId ? press.n : 0;
  // A stage node, or a cell of the table's pipeline strip, opens that stage
  // of the isolate in place (IsolatePeek, ?peek=), the row highlighted under it.
  const onPeek = useCallback((id, stage) => {
    onParams({ hl: id, peek: stage });
    ensure(id);
  }, [onParams, ensure]);
  const closePeek = useCallback(() => onParams({ peek: null }), [onParams]);
  const openRow = useCallback((id) => {
    onParams({ peek: null, hl: id, open: id });
    setPress((p) => ({ id, n: p.n + 1 }));
  }, [onParams]);
  useEffect(() => { if (params.peek && params.hl) ensure(params.hl); }, [params.peek, params.hl, ensure]);
  const peekItem = params.peek && params.hl ? (overview.items || []).find((i) => i.specimen_id === params.hl) || null : null;
  // A row asked for from outside the hub -- Home's search names it (`reveal`,
  // with the URL already naming it as hl=) -- is pressed the same way: its
  // row is brought into view once it is there.
  useEffect(() => {
    if (reveal?.id) setPress((p) => ({ id: reveal.id, n: p.n + 1 }));
  }, [reveal?.id, reveal?.n]);

  // The table's and the pipelines' own writes. A search or another page
  // moves the rows on, and the highlight goes with them.
  const onRowsParams = useCallback((patch) => onParams(
    'q' in patch || 'page' in patch ? { hl: null, ...patch } : patch,
  ), [onParams]);

  if (!visible || forbidden) return null;

  const setCanvas = (mode) => {
    storeCanvas(mode);
    if (mode === 'isolates') {
      if (!isolatesMode) onParams(TO_ISOLATES);
      return;
    }
    if (!isolatesMode) return;
    // Back to the totals. The bare "active" filter the switch set goes with
    // it; one the reader chose since (a state chip, a unit) stays.
    const bareLab = params.view === 'lab' && !params.node && !params.state && !params.unit && !params.origin;
    onParams({ canvas: null, hl: null, ...(bareLab ? { view: null, page: null } : {}) });
  };

  const selected = selectedNode({ ...params, view });
  const clear = Object.fromEntries(FILTER_PARAMS.map((k) => [k, null]));
  const openNode = (key) => {
    const target = nodeParams(key);
    // A pressed node's header lifts its filter: the whole table again.
    onParams(selected === key ? clear : { ...clear, ...target });
  };
  const sameAs = (target) => !!target && Object.entries(target).every(([k, v]) => (k === 'view' ? view : params[k]) === v)
    && ['node', 'state', 'unit', 'origin'].every((k) => (target[k] || '') === (params[k] || ''));
  const onState = (key, state) => {
    const target = nodeParams(key, state);
    onParams(sameAs(target) ? { ...clear, ...nodeParams(key) } : { ...clear, ...target });
  };
  const isStatePressed = (key, state) => sameAs(nodeParams(key, state));

  const canField = canUseWorkspace(user, 'field');
  const fieldState = { state: { returnTo: '/' } };
  // "New intake" opens the intake form here, in a drawer (IntakeDrawer):
  // taking something in never leaves Home. The Intake node's button calls
  // it, and nothing else does.
  const onNewIntake = canDo(user, 'newIntake') ? () => setIntakeOpen(true) : null;
  // The intake made, the table shows its isolate -- searched by its code
  // under no other filter, so whichever population it starts in (awaiting,
  // in culture, in the bank) it is the row there, highlighted and brought
  // into view. A step that failed after the record existed (the plate, the
  // tubes) is said instead of the success: the isolate is there, awaiting
  // the lab. Report only makes no isolate: the toast says so.
  const onIntakeDone = ({ specimen, sourceType, warnings = [] } = {}) => {
    setIntakeOpen(false);
    const failed = warnings.find((w) => w.kind === 'step') || warnings[0];
    if (specimen?.specimen_code) {
      say(failed?.message || t('toast.intakeRegistered', { code: specimen.specimen_code, lab: specimen.lab_code || '' }));
      onParams({ ...clear, q: specimen.specimen_code, hl: specimen.specimen_id });
      setPress((p) => ({ id: specimen.specimen_id, n: p.n + 1 }));
    } else {
      // Material whose isolate the server did not register (it never fails
      // the intake for that) is not a report: say which it was.
      say(failed?.message || t(sourceType && sourceType !== 'info_only' ? 'toast.intakeNoIsolate' : 'toast.intakeSaved'));
    }
    loadHub();
  };

  // "+ Add isolate / dish": whoever may take something in or collect in the
  // field (AddIsolateModal.canAdd). An intake made there ends as one made
  // from the Intake node does; a field collection is on this device and on
  // its way, which the toast says, and the Collection node's unsent count
  // shows until the outbox has sent it.
  const addable = canAdd(user);
  const onAddIntakeDone = (result) => {
    setAddOpen(false);
    onIntakeDone(result);
  };
  const onFieldSaved = (_batch, { queued } = {}) => {
    setAddOpen(false);
    say(t(queued ? 'add.fieldQueued' : 'add.fieldSending'));
    loadHub();
    overview.reload?.();
  };
  // Plates started for an existing field collection from the add dialog: the
  // table is searched for that isolate, so the new plates are the row in view.
  const onPlatesStarted = (message, collection) => {
    setAddOpen(false);
    say(message);
    loadHub();
    const code = collection?.specimen_code || collection?.qr_uid;
    if (code) onParams({ q: code, hl: null, page: null });
    else overview.reload?.();
  };
  const onOpenIsolate = (code, path) => {
    setAddOpen(false);
    if (path) { navigate(path, { state: { returnTo: '/' } }); return; }
    if (code) onParams({ q: code, hl: null, page: null });
  };

  return (
    <section className="hub" aria-labelledby="hub-title" data-testid="isolate-hub">
      <div className="hub-header">
        <div className="hub-header__text">
          <h2 id="hub-title" style={{ margin: 0, fontSize: 20 }}>{mineOnly ? t('titleMine') : t('title')}</h2>
          <p style={{ ...S.hint, margin: '2px 0 0', fontSize: 13 }}>{mineOnly ? t('introMine') : t('intro')}</p>
          {hub?.truncated && <p role="note" style={S.hint}>{t('hubTruncated')}</p>}
        </div>
        {addable && (
          <button type="button" className="hub-add" onClick={() => setAddOpen(true)} title={t('add.tip.button')}
            aria-haspopup="dialog" data-testid="hub-add-isolate">
            <span aria-hidden="true">+</span> {t('add.button')}
          </button>
        )}
      </div>

      {hubError && (
        <div role="alert" style={{ ...S.error, display: 'flex', gap: 10, alignItems: 'center' }}>
          {t('hubFailed')}
          <button type="button" style={S.ghostBtn} onClick={loadHub} title={t('tip.retry')}>{t('retry')}</button>
        </div>
      )}
      {!hub && !hubError && <OrganicLoader block label={t('common:loading')} slowLabel={t('common:stillWorking')} />}

      {/* "My isolates" has one view: no lab pipeline to draw an isolate along. */}
      {hub && !mineOnly && (
        <CanvasModeSwitch mode={isolatesMode ? 'isolates' : 'summary'} onChange={setCanvas} t={t} />
      )}

      {hub && isolatesMode && (
        <IsolatePipelines data={overview} params={params} onParams={onRowsParams} highlightId={highlightId}
          onHighlight={onHighlight} onPeek={onPeek} onAction={onAction} user={user} t={t} />
      )}

      {hub && !isolatesMode && (
        <PipelineCanvas hub={hub} t={t} selected={selected} triggersOnly={mineOnly}
          showIntake={!mineOnly || canDo(user, 'newIntake')}
          onOpenNode={openNode} onState={onState} isStatePressed={isStatePressed}
          unsentCount={unsent.length}
          collection={{
            canOpen: canField,
            onOpen: () => navigate('/field', fieldState),
            onMap: () => navigate('/map'),
            onGallery: canField ? () => navigate('/field/gallery', fieldState) : null,
          }}
          intake={{
            onNew: onNewIntake,
            onImport: canDo(user, 'import') ? () => navigate('/import') : null,
          }} />
      )}

      {hub && view && (
        <IsolatesTable view={view} params={params} data={overview} onParams={onRowsParams} onAction={onAction}
          onNavigate={go} user={user} t={t} dossiers={dossiers} onRefresh={refreshAfter} labScope={hub.lab_scope}
          highlightId={highlightId} highlightNonce={highlightNonce} onPeek={onPeek} onArchived={onArchived}
          onThermal={labReader ? onThermal : null}
          onStorageSaved={(item) => { say(t('toast.specimenStorageSaved')); refreshAfter(item); }} />
      )}

      {hub && thermalFor && (
        <ThermalSheet id={thermalFor} items={overview.items} dossiers={dossiers} user={user} t={t}
          onClose={toolSheet.close} onNavigate={(path) => navigate(path)} />
      )}

      {hub && params.peek && params.hl && (
        <IsolatePeek stage={params.peek} item={peekItem} entry={dossiers.get(params.hl)} user={user} t={t}
          onClose={closePeek} onOpenRow={openRow} onAction={onAction} onNavigate={go}
          onRefresh={refreshAfter} onRetry={() => dossiers.refresh(params.hl)}
          onStorageSaved={(item) => { say(t('toast.specimenStorageSaved')); refreshAfter(item); }} />
      )}

      <QuickActionDrawer action={action} entry={action ? dossiers.get(action.item.specimen_id) : null}
        user={user} t={t} onDone={onDone} onClose={closeDrawer} onNavigate={go} />
      <IntakeDrawer open={intakeOpen} user={user} onDone={onIntakeDone} onClose={() => setIntakeOpen(false)} />
      <AddIsolateModal open={addOpen} user={user} store={store} outbox={outbox} onClose={() => setAddOpen(false)}
        onIntakeDone={onAddIntakeDone} onFieldSaved={onFieldSaved} onSettled={loadHub}
        onPlatesStarted={onPlatesStarted} onOpenIsolate={onOpenIsolate} />
      {intakeId && (
        <IntakeRecordDrawer key={intakeId} externalId={intakeId} user={user} onClose={closeIntake}
          onNavigate={go} onChanged={onIntakeChanged} />
      )}

      <div role="status" aria-live="polite" className={`hub-toast${toast ? ' hub-toast--on' : ''}`}
        data-testid="hub-toast">{toast}</div>
    </section>
  );
}

/** A row's thermal captures over the hub (the tray's Thermal; ?tool=thermal&for=). */
function ThermalSheet({ id, items, dossiers, user, t, onClose, onNavigate }) {
  const dossier = dossiers.get(id).data;
  const row = (items || []).find((i) => i.specimen_id === id);
  const item = row || { specimen_id: id, specimen_code: dossier?.specimen?.specimen_code || '' };
  return (
    <Sheet side="right" title={t('tray.thermalTitle', { code: item.specimen_code || id })} onClose={onClose}
      testId="hub-thermal-sheet">
      <ThermalStrip item={item} dossier={dossier} user={user} onNavigate={onNavigate} />
    </Sheet>
  );
}
